import type { HTTPURL } from "@bracket/types";
import { Text, Icon, } from "@bracket/ui";
import { LogoLink } from "@components";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import type { FunctionComponent } from "react";
import { useRef } from "react";
import { Flow, } from "@bracket/ui/nodes";
import { getTheme, themed, } from "@bracket/ui/themes";
import styled from "styled-components";



/**
 * A promotional banner component
 * 
 * @param {PromoBarProps} props - The component props
 * @returns {JSX.Element} A promotional banner element
 */
export declare type PromoBarProps = {
	title: string;
	message: string;
	url: HTTPURL | null;
	image: HTTPURL;
	clickable?: boolean;
};



/**
 * A promotional banner component
 * 
 * @param {PromoBarProps} props - The component props
 * @returns {JSX.Element} A promotional banner element
 */
export const PromoBar: FunctionComponent<PromoBarProps> = ({
	title,
	message,
	url,
	image,
	clickable = true,
}) => {
	themed();
	const containerRef = useRef<HTMLDivElement>(null);

	const theme = getTheme();

	useGSAP(() => {
		if(containerRef.current) {
			gsap.fromTo( containerRef.current, { y: "-100%", opacity: 0, }, { y: "0%", opacity: 1, duration: 0.4, ease: "back.out(1.7)", });
		}
	}, []);


	const onClick = () => {
		if(clickable && url) {
			window.open(url, "_blank");
		}
	}

	return (
		<Brace className="PromoBar">
			<Main ref={containerRef} onClick={onClick} $clickable={clickable}>
				<InfoContainer>
					<Text size="md" color="primary" nowrap bold>{title}</Text>
					<Text size="sm" nowrap bold>{message}</Text>
				</InfoContainer>
				<LogoContainer>
					{ url && <LogoLink icon="Morpho" iconSize="110px" image={image} height="32px"/> }
				</LogoContainer>
			</Main>
			<Text size="xxsm" nowrap justify="flex-end" style={{ paddingRight: "0.75rem" }}>
			* Morpho is a third party service, use at your own risk.
			</Text>
		</Brace>
	);

};


const Brace = styled(Flow)`
	gap: 0.25rem;
	@media(min-width: 1023px) {
		margin-left: auto;
		border: 1px solid ${() => getTheme().outline_variant};
		border-radius: 8px;
		padding-bottom: 4px;
	}
`;

const Main = styled(Flow)<{ $clickable?: boolean; }>`
	align-items: center;
	gap: 1rem;
	padding: 1rem 0.75rem 1rem 1rem;
	opacity: 0;
	cursor: ${props => props.$clickable ? "pointer" : "default"};
	a { svg { scale: 0.85; } }

	// desktop below.
	@media(min-width: 1023px) {
		flex-direction: row;
		padding: 1rem 0.75rem 1rem 3rem;
	}
`;


const InfoContainer = styled(Flow)`

	// desktop below.
	@media(min-width: 1023px) {
		align-items: flex-end;
		margin-right: 0.5rem;
	}
`;

const LogoContainer = styled(Flow)`
	gap: 0.5rem;
	color: ${() => getTheme().surface_on};
`;
